/**
 * MembershipPrepayModals.tsx
 *
 * The membership detail's prepayment popups (plan 2026-10-22-001, U11):
 *   - PayAheadModal buys 1-12 more months of an active monthly membership as
 *     credits (R4, F2), paid by a card, saved or new, or a method the clinic
 *     records.
 *   - RetryPrepaymentModal charges a declined card prepayment again, the same
 *     single-payment retry the payment screen runs (chargeForwardPayment on
 *     that payment), on a saved card or a new one. Its credits become usable
 *     once it goes through (R9, AE8).
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { payAhead, type PayAheadResult, type PaymentHistoryRecord, type RecurringDetailData } from '@/services/memberships.service';
import { chargeForwardPayment, type BillingMethod, type RecurringPrepayPayload } from '@/services/payments.service';
import { getErrorMessage } from '@/services/apiClient';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import { PaymentSourceSelector } from '@/components/payments/PaymentSourceSelector';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import {
  PREPAY_BY_CARD,
  PrepayMethodFields,
  buildPrepayPayload,
  isSavedCardToken,
  prepayCardProblem,
  useRecordedPrepayMethods,
} from '@/components/memberships/PrepayMethodFields';
import { formatCurrency } from '@/lib/utils';

/** One prepayment buys 1 to 12 months (R5). */
const PAY_AHEAD_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

/** The membership's own card, when it is still a saved card on the patient. */
function membershipCard(detail: RecurringDetailData): { token: string; method: BillingMethod | null } | null {
  const pm = detail.paymentMethod;
  if (!pm || pm.active === false || !isSavedCardToken(detail.paymentMethodToken)) return null;
  return { token: detail.paymentMethodToken, method: null };
}

/* ── Pay Ahead ─────────────────────────────────────────────────────── */

export function PayAheadModal({
  detail,
  open,
  onClose,
  onPaid,
}: {
  detail: RecurringDetailData;
  open: boolean;
  onClose: () => void;
  /** The prepayment was taken, or its card declined (its credits then wait for a retry). */
  onPaid: (result: PayAheadResult) => void;
}) {
  const monthsId = useId();
  const totalLabelId = useId();
  const [months, setMonths] = useState(1);
  const [choice, setChoice] = useState(PREPAY_BY_CARD);
  const [cardChoice, setCardChoice] = useState<{ token: string; method: BillingMethod | null } | null>(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const recordedMethods = useRecordedPrepayMethods(open);

  useEffect(() => {
    if (!open) return;
    setMonths(1);
    setChoice(PREPAY_BY_CARD);
    setCardChoice(null);
    setReferenceNumber('');
    setError(null);
  }, [open]);

  const monthlyAmount = detail.amount;
  const total = Math.round(monthlyAmount * months * 100) / 100;
  const byCard = choice === PREPAY_BY_CARD;
  // The card defaults to the one the membership charges.
  const card = cardChoice ?? membershipCard(detail);
  const unusedCredits = detail.prepay?.usableCredits ?? 0;
  const handleCardChange = useCallback((token: string, method: BillingMethod | null) => {
    setCardChoice({ token, method });
  }, []);

  const handleSubmit = async () => {
    // Set synchronously, so a second click before the re-render can't charge twice.
    if (submittingRef.current) return;
    setError(null);
    let payload: RecurringPrepayPayload;
    try {
      payload = buildPrepayPayload(
        months,
        choice,
        {
          token: card?.token ?? '',
          title: card?.method ? card.method.title : detail.paymentMethod?.title,
          paymentMethodID: card?.method ? card.method.paymentMethodID : (detail.paymentMethod?.paymentMethodID || undefined),
        },
        recordedMethods,
        referenceNumber,
      );
    } catch (err) {
      setError(getErrorMessage(err));
      return;
    }

    submittingRef.current = true;
    setSaving(true);
    try {
      onPaid(await payAhead(detail.recurringPaymentId, payload));
    } catch (err) {
      setError(getErrorMessage(err, 'The prepayment could not be taken.'));
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      title="Pay Ahead"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>Back</Button>
          <Button size="sm" onClick={() => void handleSubmit()} disabled={saving} loading={saving}>
            {byCard ? `Charge ${formatCurrency(total)}` : `Record ${formatCurrency(total)}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Each month bought becomes a credit that pays one month instead of the card.
          {unusedCredits > 0
            ? ` The new credits queue behind the ${unusedCredits} unused ${unusedCredits === 1 ? 'one' : 'ones'}.`
            : ''}
        </p>

        {error && (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] items-center gap-x-4 gap-y-2 sm:grid-cols-[10rem_12rem]">
          <label htmlFor={monthsId} className="text-sm font-medium text-slate-700">
            Months
          </label>
          <select
            id={monthsId}
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {PAY_AHEAD_MONTH_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span id={totalLabelId} className="text-sm font-medium text-slate-700">
            Total
          </span>
          <output
            aria-labelledby={totalLabelId}
            className="px-3 text-base font-semibold text-slate-900 tabular-nums"
          >
            {formatCurrency(total)}
          </output>
        </div>

        <PrepayMethodFields
          patientId={detail.patientId}
          merchantId={detail.accountMerchantId ? Number(detail.accountMerchantId) : null}
          recordedMethods={recordedMethods}
          choice={choice}
          onChoiceChange={setChoice}
          cardToken={card?.token ?? ''}
          onCardChange={handleCardChange}
          referenceNumber={referenceNumber}
          onReferenceNumberChange={setReferenceNumber}
        />
      </div>
    </Modal>
  );
}

/* ── Retry a declined prepayment ───────────────────────────────────── */

export function RetryPrepaymentModal({
  detail,
  prepayment,
  onClose,
  onRetried,
}: {
  detail: RecurringDetailData;
  /** The declined prepayment line; null closes the popup. */
  prepayment: PaymentHistoryRecord | null;
  onClose: () => void;
  /** The retry went through: the prepayment's credits are usable now. */
  onRetried: () => void;
}) {
  const open = prepayment != null;
  const [cardChoice, setCardChoice] = useState<{ token: string; method: BillingMethod | null } | null>(null);
  const [merchantId, setMerchantId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const retryingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCardChoice(null);
    // The membership's merchant, which a Pay Ahead card prepayment is charged under.
    setMerchantId(detail.accountMerchantId ? Number(detail.accountMerchantId) : null);
    setError(null);
  }, [open, prepayment?.key, detail.accountMerchantId]);

  const card = cardChoice ?? membershipCard(detail);
  const amount = prepayment?.amount ?? 0;
  const handleNewCardSaved = useCallback((method: BillingMethod) => {
    setCardChoice({ token: method.token, method });
  }, []);

  const handleRetry = async () => {
    // Set synchronously, so a second click before the re-render can't charge twice.
    if (!prepayment || retryingRef.current) return;
    if (!merchantId) { setError('Select a merchant account.'); return; }
    const cardToken = card?.token ?? '';
    const cardProblem = prepayCardProblem(cardToken);
    if (cardProblem) { setError(cardProblem); return; }
    retryingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await chargeForwardPayment(prepayment.paymentId, {
        merchant_id: merchantId,
        payment_method_id: cardToken,
        amount,
        credit: 0,
        patient_id: detail.patientId,
      });
      if (result.success) {
        onRetried();
      } else {
        setError(result.message || 'Transaction error: Payment was declined.');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to retry the prepayment.'));
    } finally {
      retryingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={saving ? () => {} : onClose}
      title="Retry Prepayment"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>Back</Button>
          <Button size="sm" onClick={() => void handleRetry()} disabled={saving} loading={saving}>
            Retry {formatCurrency(amount)}
          </Button>
        </div>
      }
    >
      {prepayment ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            The card declined this prepayment of {prepayment.monthsBought} {prepayment.monthsBought === 1 ? 'month' : 'months'},
            so its credits can&apos;t be used yet. Choose a card and retry the {formatCurrency(amount)} charge; the credits
            become usable once it goes through.
          </p>
          {error && (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <PaymentSourceSelector
              label="Card"
              patientId={detail.patientId}
              value={card?.token ?? ''}
              merchantId={merchantId}
              onChange={(token, method) => setCardChoice({ token, method })}
              hideTerminals
              placeholder="Choose a card"
            />
            <MerchantSelector
              value={merchantId}
              onChange={(id) => setMerchantId(id)}
              preferredFacilityId={detail.facilityID || null}
            />
          </div>
          <ForwardBillingMethodCapture
            active={card?.token === 'new'}
            patientId={detail.patientId}
            merchantId={merchantId}
            onSaved={handleNewCardSaved}
          />
        </div>
      ) : null}
    </Modal>
  );
}
